import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CurrencyDollar, TrendUp, CalendarBlank, ChartBar, Trash, Plus } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Line, Bar, PieChart, Pie, Cell } from "recharts";
import DatePicker from "../components/DatePicker";

const DEFAULT_SALES_GROUPS = [
  { group: "Food Sales", color: "#2F4F4F", items: [
    { key: "brunch", label: "Brunch" }, { key: "lunch", label: "Lunch" }, { key: "food", label: "Dinner" },
    { key: "kids_menu", label: "Kids Menu" }, { key: "employees_menu", label: "Employees Menu" },
    { key: "turn_menu", label: "Turn Menu" }, { key: "side_orders", label: "Side Orders" },
    { key: "dessert", label: "Dessert" }, { key: "bev_cart_fb", label: "Bev Cart F&B" },
    { key: "z_pet_fb", label: "Z PE&T F&B" },
  ]},
  { group: "Wine Sales", color: "#E07A5F", items: [
    { key: "wine_by_the_glass", label: "Wine by the Glass" },
    { key: "wine_bottles", label: "Wine Bottles - Red" }, { key: "wine_bottles_white", label: "Wine Bottles - White" },
  ]},
  { group: "Liquor Sales", color: "#F4A261", items: [
    { key: "rum_and_tequila", label: "Rum & Tequila" }, { key: "vodka_and_gins", label: "Vodka & Gins" },
    { key: "bourbon", label: "Bourbon" }, { key: "whiskey_and_scotch", label: "Whiskey & Scotch" },
    { key: "bev_cart_liquor", label: "Bev Cart Liquor" }, { key: "mixed_drinks", label: "Mixed Drinks" },
  ]},
  { group: "Beer Sales", color: "#81B29A", items: [
    { key: "beer", label: "Beer" }, { key: "bev_cart_beer", label: "Bev Cart Beer" },
  ]},
  { group: "Other", color: "#6D6875", items: [
    { key: "soft_drinks", label: "Soft Drinks" }, { key: "events", label: "Events" },
  ]},
];

export default function SalesPage() {
  const { restId } = useRestaurant();
  const [sales, setSales] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);
  const [showAdd, setShowAdd] = useState(false);
  const [formDate, setFormDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [formCats, setFormCats] = useState({});
  const [formNotes, setFormNotes] = useState("");
  const [salesGroups, setSalesGroups] = useState(DEFAULT_SALES_GROUPS);

  // Fetch custom sales categories
  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get(`/sales/categories`);
      const groups = Array.isArray(data) ? data : data?.groups;
      if (groups && groups.length > 0) setSalesGroups(groups);
    } catch { /* use defaults */ }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const allKeys = salesGroups.flatMap((g) => g.items.map((i) => i.key));
  const emptyCats = () => allKeys.reduce((o, k) => ({ ...o, [k]: "" }), {});
  const calcGroupTotal = (cats, group) => group.items.reduce((s, i) => s + (parseFloat(cats[i.key]) || 0), 0);

  const fetchSales = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}&days=${days}` : `?days=${days}`;
      const { data } = await api.get(`/sales${params}`);
      setSales(data);
    } catch { toast.error("Failed to load sales"); }
  }, [restId, days]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}&days=${days}` : `?days=${days}`;
      const { data } = await api.get(`/sales/analytics${params}`);
      setAnalytics(data);
    } catch { /* silent */ }
  }, [restId, days]);

  useEffect(() => { fetchSales(); fetchAnalytics(); }, [fetchSales, fetchAnalytics]);

  const addSales = async () => {
    const catData = {};
    Object.entries(formCats).forEach(([k, v]) => { catData[k] = parseFloat(v) || 0; });
    const hasAny = Object.values(catData).some((v) => v > 0);
    if (!hasAny) return;
    try {
      await api.post("/sales", { restaurant_id: restId, date: formDate, ...catData, notes: formNotes });
      setShowAdd(false);
      setFormCats(emptyCats());
      setFormNotes("");
      fetchSales();
      fetchAnalytics();
      toast.success("Sales recorded");
    } catch { toast.error("Failed"); }
  };

  const deleteSale = async (id) => {
    try {
      await api.delete(`/sales/${id}`);
      fetchSales();
      fetchAnalytics();
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const agg = analytics?.aggregate || {};
  const daily = analytics?.daily || [];
  const catTotals = analytics?.category_totals || {};

  // Subtotal pie data using groups
  const pieData = salesGroups.filter((g) => g.group !== "Other").map((g) => {
    const val = g.items.reduce((s, i) => s + (catTotals[i.key] || 0), 0);
    return { name: g.group, value: val, color: g.color };
  }).filter((d) => d.value > 0);

  const formTotal = allKeys.reduce((s, k) => s + (parseFloat(formCats[k]) || 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="sales-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <CurrencyDollar size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Sales</h2>
          <Badge className="bg-red-100 text-red-700 text-[10px]">Admin Only</Badge>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-stone-100 rounded-lg p-0.5">
            {[7, 14, 30, 90].map((d) => (
              <button key={d} className={`text-xs px-3 py-1.5 rounded-md font-medium transition-all ${days === d ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`} onClick={() => setDays(d)} data-testid={`sales-period-${d}`}>
                {d}d
              </button>
            ))}
          </div>
          <Button onClick={() => { setFormCats(emptyCats()); setFormNotes(""); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} size="sm" className="text-xs" data-testid="add-sales-btn">
            <Plus size={14} className="mr-1" /> Add Entry
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="sales-summary">
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Sales</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>${agg.total_sales?.toLocaleString() || "0"}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Avg / Day</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#81B29A" }}>${agg.avg_daily_sales?.toLocaleString() || "0"}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Revenue / Cover</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#F4A261" }}>${agg.avg_revenue_per_cover || "0"}</p>
        </div>
        <div className="bg-white rounded-lg border border-stone-200 p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Best Day</p>
          <p className="text-lg font-bold mt-1" style={{ fontFamily: "Work Sans", color: "#E07A5F" }}>
            {agg.best_day ? `$${agg.best_day.total_sales.toLocaleString()}` : "--"}
          </p>
          {agg.best_day && <p className="text-[10px] text-stone-400">{agg.best_day.label}</p>}
        </div>
      </div>

      {/* Subtotal Cards */}
      {Object.keys(catTotals).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {salesGroups.filter((g) => g.group !== "Other").map((g) => {
            const sub = g.items.reduce((s, i) => s + (catTotals[i.key] || 0), 0);
            return (
              <div key={g.group} className="bg-white rounded-lg border border-stone-200 p-3 text-center" style={{ borderLeftColor: g.color, borderLeftWidth: "3px" }}>
                <p className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">{g.group}</p>
                <p className="text-lg font-bold mt-0.5" style={{ fontFamily: "Work Sans", color: g.color }}>${sub.toLocaleString()}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {daily.length > 0 && (
          <div className="bg-white rounded-lg border border-stone-200 p-5 lg:col-span-2" data-testid="sales-trend-chart">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
              <TrendUp size={16} weight="duotone" /> Sales Trend
            </h3>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily}>
                  <defs>
                    <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2F4F4F" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2F4F4F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#57534E" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Area type="monotone" dataKey="total_sales" stroke="#2F4F4F" strokeWidth={2} fill="url(#salesFill)" name="Sales" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {pieData.length > 0 && (
          <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="category-pie">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4">Category Breakdown</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} stroke="none" />)}
                  </Pie>
                  <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {pieData.map((d) => (
                <span key={d.name} className="flex items-center gap-1 text-[10px] text-stone-500">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sales History */}
      <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="sales-history">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <CalendarBlank size={16} weight="duotone" /> Sales History
        </h3>
        {sales.length === 0 ? (
          <div className="text-center py-12">
            <CurrencyDollar size={40} className="mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500 text-sm">No sales recorded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sales.map((s) => {
              const foodSub = salesGroups.find((g) => g.group === "Food Sales")?.items.reduce((t, i) => t + (s[i.key] || 0), 0) || 0;
              const wineSub = salesGroups.find((g) => g.group === "Wine Sales")?.items.reduce((t, i) => t + (s[i.key] || 0), 0) || 0;
              const liquorSub = salesGroups.find((g) => g.group === "Liquor Sales")?.items.reduce((t, i) => t + (s[i.key] || 0), 0) || 0;
              const beerSub = salesGroups.find((g) => g.group === "Beer Sales")?.items.reduce((t, i) => t + (s[i.key] || 0), 0) || 0;
              return (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`sale-row-${s.id}`}>
                  <div>
                    <span className="font-medium text-stone-800 text-sm">{s.date}</span>
                    {s.notes && <span className="text-[10px] text-stone-400 ml-2">{s.notes}</span>}
                    <div className="flex gap-3 mt-1 text-[10px]">
                      {foodSub > 0 && <span style={{ color: "#2F4F4F" }}>Food ${foodSub}</span>}
                      {wineSub > 0 && <span style={{ color: "#E07A5F" }}>Wine ${wineSub}</span>}
                      {liquorSub > 0 && <span style={{ color: "#F4A261" }}>Liquor ${liquorSub}</span>}
                      {beerSub > 0 && <span style={{ color: "#81B29A" }}>Beer ${beerSub}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold" style={{ color: "#2F4F4F" }}>${s.total_sales?.toLocaleString()}</span>
                    <button onClick={() => deleteSale(s.id)} className="text-stone-300 hover:text-red-500"><Trash size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Sales Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-sales-dialog">
          <DialogHeader><DialogTitle>Add Sales Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <DatePicker value={formDate} onChange={setFormDate} testId="sales-date-input" />
            {salesGroups.map((g) => (
              <div key={g.group} className="space-y-1.5">
                <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold">{g.group}</p>
                {g.items.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-[11px] text-stone-600 font-medium w-40 text-right">{label}</span>
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs">$</span>
                      <Input type="number" step="0.01" min="0" placeholder="0.00" value={formCats[key]} onChange={(e) => setFormCats((p) => ({ ...p, [key]: e.target.value }))} className="pl-5 h-7 text-xs" data-testid={`sales-form-${key}`} />
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between py-1 px-2 rounded-md" style={{ backgroundColor: g.color + "12" }}>
                  <span className="text-[10px] font-bold uppercase" style={{ color: g.color }}>{g.group}</span>
                  <span className="text-xs font-bold" style={{ color: g.color }}>${calcGroupTotal(formCats, g).toFixed(2)}</span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-[#2F4F4F] text-white">
              <span className="text-xs font-bold uppercase tracking-widest">Total Sales</span>
              <span className="text-xl font-bold">${formTotal.toFixed(2)}</span>
            </div>
            <Input placeholder="Notes (optional)" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} data-testid="sales-form-notes" />
            <Button className="w-full" style={{ backgroundColor: "#2F4F4F" }} onClick={addSales} data-testid="save-sales-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
